import { useState } from "react";
import { FaLeaf, FaSeedling, FaTree } from "react-icons/fa";
import { PiPottedPlantFill } from "react-icons/pi";
import { useDispatch, useSelector } from "react-redux";
import { createSendMessageToAPI } from "../../../abstract/api/rest";
import { useYamlImport } from "../../../abstract/hook/useYamlImport";
import { getEndpointAI } from "../../../abstract/redux/slice/endpointSlice";
import { getShowPageGenerator } from "../../../abstract/redux/slice/generatorSlice";
import { getSpinner, setSpinner } from "../../../abstract/redux/slice/overlaySlice";
import ButtonIntro from "../../ui/buttons/ButtonIntro";
import ButtonIntroWide from "../../ui/buttons/ButtonIntroWide";
import ButtonTomatoClose from "../../ui/buttons/ButtonTomatoClose";
import DividerDefault from "../../ui/dividers/DividerDefault";
import { useSharedChatStorage } from "../panel/ai/context/useSharedChatStorage";
import styles from "./Splash.module.scss";

const _defPrompt = "Generate a Tomato model of dogs in a galaxy far, far away...";

function Splash({ tutorial, handleSetTutorial, setShowAsideRight }) {
  const showGenerator = useSelector(getShowPageGenerator);
  const isLoading = useSelector(getSpinner);

  if (tutorial === 0) {
    return <Intro1 handleSetTutorial={handleSetTutorial} setShowAsideRight={setShowAsideRight} />;
  }

  if (tutorial === 1 && !isLoading) {
    return <Intro2 handleSetTutorial={handleSetTutorial} />;
  }

  if (tutorial === 2 && showGenerator) {
    return <Intro3 handleSetTutorial={handleSetTutorial} />;
  }

  return null;
}

function Intro1({ handleSetTutorial, setShowAsideRight }) {
  const dispatch = useDispatch();
  const _endpointAI = useSelector(getEndpointAI);
  const [_prompt, _setPrompt] = useState(_defPrompt);
  const { loadFromText, loadPredefinedModel } = useYamlImport();
  const { setMessages } = useSharedChatStorage();

  const updateInitial = (model) => {
    loadPredefinedModel(model);

    handleSetTutorial(1);
  };

  const handleClose = () => {
    handleSetTutorial(2);
  };

  const handlePromptChange = (e) => {
    const _value = e.target.value;

    _setPrompt(_value);
  };

  const handlePromptGenerate = async (e) => {
    handleSetTutorial(1);
    dispatch(setSpinner(true));

    const response = await createSendMessageToAPI(_prompt, _endpointAI);

    if (response?.user) {
      setMessages((prev) => [...prev, response.user]);
    }

    if (response?.bot?.hasValidModel) {
      loadFromText(response?.bot?.model);
    }

    if (response?.bot?.text) {
      setMessages((prev) => [...prev, response.bot]);
    }

    setShowAsideRight(1);
  };

  return (
    <div className={`${styles["background"]} ${styles["background--1"]}`}>
      <div className={`${styles["foreground"]} ${styles["foreground--1"]}`}>
        <ButtonTomatoClose onClick={handleClose} />
        <div className={styles["header"]}>
          <div className={styles["header__assistant"]}></div>
          <div className={styles["header__text--main"]}>
            <p>
              <b className={styles["color--green-dark"]}>Welcome!</b> It looks like you're fresh off
              the vine and visiting for the first time.
            </p>
            <p>
              My name is <b>ABC</b>, and I'm here to help you! First things first: we need to
              establish a model. You can assemble one manually from scratch, but if you don't want
              to, here are two shortcuts:
            </p>
            <ul>
              <li>
                <FaLeaf className={styles["color--green"]} />
                <b>Pick from the vine:</b> Use one of our fully-ripe predefined models (click a
                model button below).
              </li>
              <li>
                <FaSeedling className={styles["color--green"]} />
                <b>Let AI do the planting:</b> Have our AI agent instantly sprout a custom model for
                you (click 'Generate').
              </li>
            </ul>
          </div>
        </div>
        <div className={styles["predefined"]}>
          <div className={styles["predefined__description"]}>Pick one of example models:</div>
          <div className={styles["predefined__tutorial"]}>
            To see their detailed description, visit the&nbsp;
            <a
              href="https://testify.no/en/tomato-en/tutorials-en/"
              target="_blank"
              rel="noreferrer">
              tutorial page
            </a>
            &nbsp;
          </div>
          <div className={styles["predefined__options"]}>
            <ButtonIntro
              theme="green"
              icon={<FaSeedling />}
              title="Simple"
              subtitle="Quick and beginner-friendly"
              onClick={() => updateInitial("tutorial1")}
            />
            <ButtonIntro
              theme="blue"
              icon={<PiPottedPlantFill />}
              title="Intermediate"
              subtitle="More tools for custom growth"
              onClick={() => updateInitial("tutorial2")}
            />
            <ButtonIntro
              theme="orange"
              icon={<FaTree />}
              title="Advanced"
              subtitle="Full control for complex challenges"
              onClick={() => updateInitial("tutorial3")}
            />
          </div>
        </div>
        <DividerDefault lineColor="#9BB867">
          <div className={styles["divider"]}>OR</div>
        </DividerDefault>
        <div className={styles["llm"]}>
          <div className={styles["llm__description"]}>
            Describe what you want to model and let our AI assistant create it for you:
          </div>
          <div className={styles["llm__panel"]}>
            <textarea
              value={_prompt}
              className={styles["llm__input"]}
              rows={5}
              onChange={handlePromptChange}
            />
            <ButtonIntroWide theme="tomato" title="Generate" onClick={handlePromptGenerate} />
          </div>
        </div>
        <DividerDefault lineColor="#9BB867">
          <div className={styles["divider"]}>OR</div>
        </DividerDefault>
        <div className={styles["manual"]}>
          <div className={styles["manual__description"]}>Just start modeling!</div>
        </div>
        <div className={styles["confirmation"]}>
          <ButtonIntroWide theme="tomato" title="Got it!" onClick={handleClose} />
        </div>
      </div>
    </div>
  );
}

function Intro2({ handleSetTutorial }) {
  const handleClose = () => {
    handleSetTutorial(2);
  };

  return (
    <div className={`${styles["background"]} ${styles["background--2"]}`}>
      <div className={`${styles["foreground"]} ${styles["foreground--2"]}`}>
        <ButtonTomatoClose onClick={handleClose} />
        <div className={styles["header"]}>
          <div className={styles["header__assistant"]}></div>
          <div className={styles["header__text--main"]}>
            <p>
              <b className={styles["color--green-dark"]}>Perfectly planted!</b> Your model has taken
              root. Now it's time to use it to grow some tests!
            </p>
            <p>
              Just a quick heads-up: the{" "}
              <b className={styles["color--green-dark"]}>Generate Tests</b> button is always waiting
              for you in the bottom-right corner of your screen.
            </p>
            <p>
              Take some time to explore your new model. Whenever you're ready, just click that
              button up top to get started!
            </p>
          </div>
        </div>
        <div className={styles["confirmation"]}>
          <ButtonIntroWide theme="tomato" title="Got it!" onClick={handleClose} />
        </div>
      </div>
    </div>
  );
}

function Intro3({ handleSetTutorial }) {
  const handleClose = () => {
    handleSetTutorial(-1);
  };

  return (
    <div className={`${styles["background"]} ${styles["background--3"]}`}>
      <div className={`${styles["foreground"]} ${styles["foreground--3"]}`}>
        <ButtonTomatoClose onClick={handleClose} />
        <div className={styles["header"]}>
          <div className={styles["header__assistant"]}></div>
          <div className={styles["header__text--main"]}>
            <p>
              <b className={styles["color--green-dark"]}>Almost time to harvest!</b> You're now
              ready to generate your test cases.
            </p>
            <p>
              By default, we use the <b>N-Wise generator</b>. It perfectly pairs up your choices to
              ensure great coverage, which is usually all you need for a healthy batch of tests! (If
              you're curious about other methods, feel free to dig into our documentation).
            </p>
            <p>
              Whenever you're ready, just hit the <b>Generate</b> button at the bottom-right corner
              of the screen. Happy testing!
            </p>
          </div>
        </div>
        <div className={styles["confirmation"]}>
          <ButtonIntroWide theme="tomato" title="Got it!" onClick={handleClose} />
        </div>
      </div>
    </div>
  );
}

export default Splash;
